import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputBase,
  InputAdornment,
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { ClearRounded } from '@material-ui/icons';

// icons
import TriangleSvg from '../icons/triangle';

const CustomInput = withStyles((theme) => ({
  root: {
    'label + &': {
      marginTop: theme.spacing(3),
    },
  },
  input: {
    borderRadius: 6,
    position: 'relative',
    backgroundColor: 'var(--grey-025)',
    border: '1px solid var(--grey-300)',
    fontSize: 14,
    color: 'var(--grey-900)',
    padding: '12px 14px',
    transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color']),
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    height: '1.1876em',
    '&:focus': {
      borderRadius: 6,
      borderColor: 'var(--brand-700)',
      backgroundColor: 'var(--grey-025)',
    },
  },
}))(InputBase);

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: '100%',
  },
  label: {
    transform: 'translate(0px, 5px) scale(0.9)',
    color: 'var(--brand-700)',
    fontWeight: 500,
    fontSize: 14,
  },
  menuPaper: {
    marginTop: 4,
    borderRadius: 8,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    border: '1px solid var(--grey-300)',
    padding: '0 0.5rem',
  },
  menuItem: {
    fontSize: 14,
    fontWeight: 500,
    padding: '0.5rem 1rem',
    color: 'var(--grey-700)',
    borderRadius: 8,
    minHeight: '0',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: '1.4',
    '&:hover': {
      backgroundColor: 'var(--grey-050)',
      borderRadius: 8,
    },
  },
  iconWrapper: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '0.5rem',
    width: '0.5rem',
    color: 'var(--info-500)',
  },
  svg: {
    height: 10,
    width: 10,
    fill: '#a9b0bf',
    transition: 'all 0.8s',
  },
  svgTransform: {
    height: 10,
    width: 10,
    fill: 'black',
    transform: 'rotateX(0deg)',
    transition: 'all 0.8s',
  },
  iconClosed: {
    transform: 'translateY(-50%) rotate(0deg)',
  },
  iconOpen: {
    transform: 'translateY(-50%) rotate(180deg)',
  },
  clearIcon: {
    cursor: 'pointer',
    height: 18,
    width: 18,
    color: '#8c95a3',
    '&:hover': {
      color: '#333',
    },
  },

  selectInner: {
    overflow: 'hidden',
  },
  scrollableValue: {
    display: 'block',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    width: '100%',

    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
}));

const Dropdown = ({
  error,
  name,
  data,
  onChange,
  info = <></>,
  disabled,
  raisedPlaceHolder = true,
  allowClear = false,
  containerStyles = {},
  value,
  zIndex = 9999,
  placeholder = '',
  ...props
}) => {
  const classes = useStyles();

  const containerRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState(0);

  const [internalValue, setInternalValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const currentValue = value !== undefined ? value : internalValue;

  const getDisplayValue = (content) => {
    return typeof content?.name === 'string' ? content.name : content;
  };

  const handleChange = (event) => {
    const val = event.target.value;
    const selectedItem = data?.find((item) => getDisplayValue(item) === val);

    onChange(selectedItem !== undefined ? selectedItem : val);

    if (value === undefined) {
      setInternalValue(val);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    if (value === undefined) {
      setInternalValue('');
    }
  };

  const handleOpen = () => {
    if (containerRef.current) {
      setMenuWidth(containerRef.current.offsetWidth);
    }
    setIsOpen(true);
  };

  const DropdownIcon = () => {
    return (
      <div className={`${classes.iconWrapper} ${isOpen ? classes.iconOpen : classes.iconClosed}`}>
        <TriangleSvg className={isOpen ? classes.svgTransform : classes.svg} />
      </div>
    );
  };

  return (
    <FormControl
      ref={containerRef}
      className={classes.formControl}
      style={containerStyles}
      error={Boolean(error)}
      disabled={disabled}
    >
      {placeholder && raisedPlaceHolder && (
        <InputLabel
          id={`${name}-label`}
          classes={{ root: classes.label }}
          shrink={true}
          disableAnimation={false}
          style={{
            opacity: currentValue ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
            pointerEvents: currentValue ? 'auto' : 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {placeholder}
            {info}
          </div>
        </InputLabel>
      )}

      <Select
        labelId={placeholder && raisedPlaceHolder ? `${name}-label` : undefined}
        name={name}
        value={currentValue || ''}
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={() => setIsOpen(false)}
        displayEmpty
        input={<CustomInput />}

        classes={{ select: classes.selectInner }}

        IconComponent={DropdownIcon}
        MenuProps={{
          style: { zIndex },
          classes: { paper: classes.menuPaper },
          getContentAnchorEl: null,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          PaperProps: {
            style: {
              maxHeight: 250,
              width: menuWidth > 0 ? menuWidth : 'auto',
            },
          },
        }}
        endAdornment={
          allowClear && currentValue ? (
            <InputAdornment position="end" style={{ position: 'absolute', right: 32 }}>
              <ClearRounded className={classes.clearIcon} onClick={handleClear} />
            </InputAdornment>
          ) : null
        }

        renderValue={(selected) => {
          return (
            <div className={classes.scrollableValue}>
              {!selected ? (
                <span style={{ color: '#8c95a3' }}>{placeholder}</span>
              ) : (
                selected
              )}
            </div>
          );
        }}
        {...props}
      >
        {data?.map((content, idx) => {
          const displayVal = getDisplayValue(content);
          return (
            <MenuItem key={idx} value={displayVal} className={classes.menuItem}>
              {displayVal}
            </MenuItem>
          );
        })}
      </Select>

      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default React.memo(Dropdown);